import { computed, getCurrentScope, onScopeDispose, shallowRef, type Ref } from 'vue';
import type { ProductSearch } from '@/entities/product';
import type { SearchCriteria } from '@/entities/searchCriteria';
import { getSearchFormData } from '@/shared/api';
import type { LowPriceDay } from '@/shared/api/searchLatest';
import { createUuid } from '@/shared/lib/createUuid';
import type { DateRange, DepartureCity, SearchFormData } from '@/shared/model';
import { getLowPriceMinDate } from '@/widgets/lowPriceCalendar';
import DepartureCityState from './alternativeSearch/DepartureCityState';
import type {
  AlternativeSearchContext,
  AlternativeSearchState,
  AlternativeSearchView,
} from './alternativeSearch/types';
import { hasSerpProducts } from '../lib/toSerpProducts';

export interface AlternativeSearchModel {
  view: Readonly<Ref<AlternativeSearchView | null>>;
  alternativeSearchCriteria: Readonly<Ref<SearchCriteria | null>>;
  userSearchUuid: Readonly<Ref<string | null>>;
  search(criteria: SearchCriteria, withLowLimit?: boolean): void;
  selectDepartureCity(city: DepartureCity): void;
}

export interface AlternativeSearchCalendar {
  days: Ref<LowPriceDay[]>;
  isLoading: Ref<boolean>;
  isAvailable: Ref<boolean>;
}

interface AlternativeSearchOptions {
  formUrl: string;
  calendar: AlternativeSearchCalendar;
}

export function useAlternativeSearch(
  productSearch: ProductSearch,
  { formUrl, calendar }: AlternativeSearchOptions,
): AlternativeSearchModel {
  const state = shallowRef<AlternativeSearchState | null>(null);
  const userSearchUuid = shallowRef<string | null>(null);

  let userCriteria: SearchCriteria | null = null;
  let formData: SearchFormData | null = null;

  const loadFormData = async (): Promise<void> => {
    formData = await getSearchFormData(formUrl).catch(() => formData);
  };

  const getCalendarMinPriceDate = (excluded: readonly DateRange[]): string | null =>
    calendar.isAvailable.value && !calendar.isLoading.value ? getLowPriceMinDate(calendar.days.value, excluded) : null;

  const setState = (next: AlternativeSearchState | null): void => {
    state.value?.dispose?.();
    state.value = next;
    next?.start?.();
  };

  const createContext = (criteria: SearchCriteria): AlternativeSearchContext => ({
    productSearch,
    userCriteria: criteria,
    formData,
    getCalendarMinPriceDate,
    setState,
  });

  const search = (criteria: SearchCriteria, withLowLimit?: boolean): void => {
    setState(null);
    userCriteria = criteria;
    userSearchUuid.value = createUuid();
    loadFormData();
    productSearch.run(criteria, { searchUuid: userSearchUuid.value, withLowLimit });
  };

  const selectDepartureCity = (city: DepartureCity): void => {
    if (state.value instanceof DepartureCityState) state.value.selectCity(city);
  };

  const unsubscribe = productSearch.on('end', (result) => {
    if (state.value || !userCriteria || result.state !== 'finished' || hasSerpProducts(result)) return;

    setState(new DepartureCityState(createContext(userCriteria)));
  });

  if (getCurrentScope()) {
    onScopeDispose(() => {
      unsubscribe();
      state.value?.dispose?.();
    });
  }

  return {
    view: computed(() => state.value?.view ?? null),
    alternativeSearchCriteria: computed(() => state.value?.criteria ?? null),
    userSearchUuid,
    search,
    selectDepartureCity,
  };
}
